import { get, post, fetchJSON, postProtected, getProtected } from './utils';

/**
 * @typedef UserModel
 * @type {object}
 * @property {string} Username
 * @property {string} Password
 * @property {string} Email
 * @property {string} NickName
 * @property {string} BirthDate
 * @property {string} FirstName
 * @property {string} LastName
 * @property {string} Address1
 * @property {string} Address2
 * @property {string} City
 * @property {string} Zip
 * @property {string} Region
 * @property {string} Country
 * @property {string} PhoneNumber
 * @property {string} MobilePhoneNumber
 * @property {string} Language
 * @property {string} [Gender=Male]
 * @property {string} [Currency=Default]
 * @property {boolean} [ReceiveNewsletters=true]
 * @property {boolean} [ReceiveOffers=true]
 * @property {string} PromoCode
 * @property {string} AffiliateTag
 */

/**
 * @typedef SignInModel
 * @property {string} Username
 * @property {string} Password
 * @property {string} [Platform=Desktop]
 * @property {string} [OperatingSystem]
 * @property {string} [Browser]
 */

/**
 * Register a new player in the system and creates a wallet in the supplied currency, or in the default currency if none is supplied
 * @param {UserModel} userInfo
 * @returns {int} Player ID
 */
export var signUp = function signUp(userInfo) {
  return post('/player/signup', userInfo);
};

/**
 * Register a new player in the system and creates a wallet in the supplied currency, or in the default currency if none is supplied
 * @param {UserModel} userInfo
 * @returns {Object} Object containing Player ID and Issues
 */
export var signUpV2 = function signUpV2(userInfo) {
  return post('/player/signup/v2', userInfo);
};

/**
 * Register a new player in the system using a auth code provided via third party auth service and creates a wallet in the supplied currency, or in the default currency if none is supplied.
 * @param {UserModel} userInfo
 * @returns {Object} Object containing Player ID and Issues
 */
export var signUpWithCode = function signUpWithCode(userInfo) {
  return post('/player/signup/code', userInfo);
};

export var signUpWithToken = function signUpWithToken(userInfo) {
  return post('/player/signup/token', userInfo);
};

/**
 * Update the current players info, the players current password is required to authenticate the change
 * @param {UserModel} user
 * @param {string} playerSessionID
 */
export var update = function update(user, playerSessionID) {
  return postProtected('/player/update', playerSessionID, user);
};

/**
 * Get all saved user details for the currently authenticated user
 * @param {string} playerSessionID
 * @returns {UserModel}
 */
export var userInfo = function userInfo(playerSessionID) {
  return getProtected('/player/user-info', playerSessionID);
};

/**
 * Change player password
 * @param {Object} passwords an object contains curernt password and new password
 * @param {string} passwords.CurrentPassword Current Password
 * @param {string} passwords.NewPassword New password
 * @param {string} playerSessionID
 */
export var changePassword = function changePassword(passwords, playerSessionID) {
  return postProtected('/player/change-password', playerSessionID, passwords);
};

/**
 * Authenticates a player and creates a new session. Failed sign in attemps will cause the client to be rate limited, ie he can not try to sign in again until some time has passed. The time between subsequent signins is calculated using the following formula (n - 1) ^ 2 seconds, where n is the number of attempts The rate limit is reset on successfully login and is tracked by ip and username
 * @param {SignInModel} user
 * @returns {string} Session ID
 */
export var signIn = function signIn(user) {
  return post('/player/signin', user);
};

/**
 * Authenticates a player and creates a new session and TacVersionAccepted. Failed sign in attemps will cause the client to be rate limited, ie he can not try to sign in again until some time has passed. The time between subsequent signins is calculated using the following formula (n - 1) ^ 2 seconds, where n is the number of attempts The rate limit is reset on successfully login and is tracked by ip and username
 * @param {SignInModel} user
 * @returns {string} Session ID
 */
export var signInV2 = function signInV2(user) {
  return post('/player/signin/v2', user);
};

/**
 * Authenticates a player and creates a new session and TacVersionAccepted. Failed sign in attemps will cause the client to be rate limited, ie he can not try to sign in again until some time has passed. The time between subsequent signins is calculated using the following formula (n - 1) ^ 2 seconds, where n is the number of attempts The rate limit is reset on successfully login and is tracked by ip and username
 * @param {SignInModel} user
 * @returns {string} Session ID
 */
export var signInWithCode = function signInWithCode(user) {
  return post('/player/signin/code', user);
};

/**
 * Creates a single sign on token that can be used by third party products like casino games to authenticate with the back office. The token will only be valid for a short time but at least for five minutes.
 * @param {string} playerSessionID
 * @param {('Default'|'MetricGaming'|'QuickFire'|'ISoftBet'|'SBTech'|'Yggdrasil')} tokenType
 * @returns {string}
 */
export var createSSOToken = function createSSOToken(playerSessionID) {
  var tokenType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'Default';
  return postProtected('/player/create-sso-token?tokenType=' + tokenType, playerSessionID);
};

/**
 * Invalidates the current session
 * @param {string} playerSessionID
 */
export var signOut = function signOut(playerSessionID) {
  return postProtected('/player/signout', playerSessionID);
};

/**
 * Update player Tac
 * @param {string} tacVersionAccepted
 */
export var updatePlayerTac = function updatePlayerTac(tacVersionAccepted, playerSessionID) {
  return fetchJSON('/player/update-player-tac?tacVersionAccepted=' + tacVersionAccepted, {
    headers: {
      'X-Player-Session-Id': playerSessionID
    }
  });
};

/**
 * Request password reset
 * @param {string} UsernameOrEmail
 */
export var requestPasswordReset = function requestPasswordReset(UsernameOrEmail) {
  return post('/player/request-password-reset', { UsernameOrEmail: UsernameOrEmail });
};

/**
 * Resets the players password provided a password reset token and a new password. The token will be destroyed upon a successfull reset.
 * @typedef ResetPasswordModel
 * @type {object}
 * @property {string} UsernameOrEmail
 * @property {string} Token
 * @property {string} NewPassword
 * @param {ResetPasswordModel} resetPasswordModel
 */
export var resetPassword = function resetPassword(resetPasswordModel) {
  return post('/player/reset-password', resetPasswordModel);
};

/**
 * Activate the player account using a code sent to the player by email or sms.
 * @param {string} UsernameOrEmail
 */
export var activate = function activate(UsernameOrEmail, Code, DeviceDetails) {
  return post('/player/activate', { UsernameOrEmail: UsernameOrEmail, Code: Code, DeviceDetails: DeviceDetails });
};

/**
 * Request a new activation code for a player, the code will be sent either by email or sms
 * @param {string} UsernameOrEmail
 */
export var requestActivationCode = function requestActivationCode(UsernameOrEmail) {
  return post('/player/request-activation-code', { UsernameOrEmail: UsernameOrEmail });
};

/**
 * Get a list of all tags currently assigned to the player
 * @param {string} playerSessionID
 */
export var getTags = function getTags(playerSessionID) {
  return getProtected('/player/tags', playerSessionID);
};

/**
 * Get all not displayed player login pages
 * @param {string} playerSessionID
 */
export var getDisplayPages = function getDisplayPages(playerSessionID) {
  return getProtected('/player/display-pages', playerSessionID);
};

/**
 * Add an existing player tag by its name (identifier) to a player
 * @param {string} Tag
 * @param {string} playerSessionID
 */
export var addTag = function addTag(Tag, playerSessionID) {
  return postProtected('/player/add-tag-to-player', playerSessionID, { Tag: Tag });
};

/**
 * Remove an existing player tag by its name (identifier) from a player
 * @param {string} Tag
 * @param {string} playerSessionID
 */
export var removeTag = function removeTag(Tag, playerSessionID) {
  return postProtected('/player/remove-tag-from-player', playerSessionID, { Tag: Tag });
};

/**
 * Get a list of all possible opt in/outs with current player status
 * @param {string} playerSessionID Session ID
 */
export var getOptInOuts = function getOptInOuts(playerSessionID) {
  return getProtected('/player/opt-in-outs', playerSessionID);
};

/**
 * Opt in player to pre-defined type
 * @param {string} playerSessionID
 */
export var optIn = function optIn(playerSessionID, Type) {
  return postProtected('/player/opt-in', playerSessionID, { Type: Type });
};

/**
 * Opt out player from pre-defined type
 * @param {string} playerSessionID
 */
export var optOut = function optOut(playerSessionID, Type) {
  return postProtected('/player/opt-out', playerSessionID, { Type: Type });
};

/**
 * Opts in the player by the optIns param. This will opt out the player from the abscent opt ins in the array.
 * @param {*} optOutToken
 * @param {*} optIns
 */
export var optOutChoices = function optOutChoices(optOutToken, optIns) {
  return post('/player/opt-out-choices', { OptOutToken: optOutToken, OptIns: optIns });
};

/**
 * Get a list of all possible opt in/outs for a player
 * @param {*} optOutToken
 */
export var getOptOutInsByToken = function getOptOutInsByToken(optOutToken) {
  return get('/player/opt-out-ins?unsubToken=' + optOutToken);
};

/**
 * Opt in player to bonus which requires that
 * @param {string} PlayerId
 * @param {string} BonusId
 * @param {string} playerSessionID
 */
export var bonusOptIn = function bonusOptIn(PlayerId, BonusId, playerSessionID) {
  return postProtected('/player/bonus-opt-in', playerSessionID, { PlayerId: PlayerId, BonusId: BonusId });
};

/**
 * Opt in player to tournament which requires that
 * @param {string} PlayerId
 * @param {string} TournamentIdentifier
 */
export var tournamentOptIn = function tournamentOptIn(PlayerId, TournamentIdentifier) {
  return post('/player/tournament-opt-in', { PlayerId: PlayerId, TournamentIdentifier: TournamentIdentifier });
};

/**
 * Change the login/username of player
 * @param {string} Username
 * @param {string} playerSessionID
 */
export var changeUsername = function changeUsername(Username, playerSessionID) {
  return postProtected('/player/change-username', playerSessionID, { Username: Username });
};

/**
 * Change the Nickname of player
 * @param {string} nickname
 * @param {string} playerSessionID
 */
export var changeNickname = function changeNickname(Nickname, playerSessionID) {
  return postProtected('/player/change-nickname', playerSessionID, {
    Nickname: Nickname
  });
};

/**
 * Get information about a players deposits and withdraws
 * @param {string} playerSessionID Session ID
 */
export var paymentStats = function paymentStats(playerSessionID) {
  return getProtected('/player/payment-stats', playerSessionID);
};

export var lifetimeStats = function lifetimeStats(playerSessionID) {
  return getProtected('/player/lifetime-stats', playerSessionID);
};

/**
 * Validate cpr
 * @param {string} cpr CPR
 */
export var validateCpr = function validateCpr(cpr) {
  return get('/player/validate-cpr?cpr=' + cpr);
};

/**
 * Set new e-mail, phone & mobile. Email is always required.
 * @param {string} Email
 * @param {string} Phone
 * @param {string} Mobile
 * @param {string} playerSessionID
 */
export var setEmailPhone = function setEmailPhone(playerSessionID, Email, Phone, Mobile) {
  return postProtected('/player/set-email-phone', playerSessionID, {
    Email: Email,
    Phone: Phone,
    Mobile: Mobile
  });
};

/**
 * Set new e-mail, phone, mobile & first name without password.
 * @param {string} Email
 * @param {string} Phone
 * @param {string} Mobile
 * @param {string} FirstName
 * @param {string} playerSessionID
 */
export var setDetailsWithoutPassword = function setDetailsWithoutPassword(playerSessionID, Email, Phone, Mobile, FirstName) {
  return postProtected('/player/set-details-without-password', playerSessionID, {
    Email: Email,
    Phone: Phone,
    Mobile: Mobile,
    FirstName: FirstName
  });
};

/**
 * Validate username
 * @param {string} username
 */
export var validateUsername = function validateUsername(username, jurisdiction) {
  return get('/player/validate-username?username=' + username + '&jurisdiction=' + jurisdiction);
};

/**
 * Validate email
 * @param {string} email
 */
export var validateEmail = function validateEmail(email, jurisdiction) {
  return get('/player/validate-email?email=' + email + '&jurisdiction=' + jurisdiction);
};

/**
 * Validate phone
 * @param {string} phone
 */
export var validatePhone = function validatePhone(phone, jurisdiction) {
  return get('/player/validate-phone?phoneNumber=' + phone + '&jurisdiction=' + jurisdiction);
};

export var verifyExistingSePlayer = function verifyExistingSePlayer(verifySePlayerModel, playerSessionID) {
  return postProtected('/player/verify-existing-se-player', playerSessionID, verifySePlayerModel);
};

export var verifyExistingSePlayerWithToken = function verifyExistingSePlayerWithToken(verifySePlayerModel, playerSessionID) {
  return postProtected('/player/verify-existing-se-player/token', playerSessionID, verifySePlayerModel);
};

export var validatePersonIdv2 = function validatePersonIdv2(personId, jurisdiction) {
  return get('/player/validate-personid/v2?personId=' + personId + '&jurisdiction=' + jurisdiction);
};

export var validateSupportNumber = function validateSupportNumber(supportNumber, jurisdiction) {
  return get('/player/validate-support-number?supportNumber=' + supportNumber + '&jurisdiction=' + jurisdiction);
};

/**
 * GET /player/active-currencyGet the display currency used for the player
 * @param {*} playerSessionID
 * @returns string
 */
export var getActiveCurrency = function getActiveCurrency(playerSessionID) {
  return getProtected('/player/active-currency', playerSessionID);
};

/**
 * Set the active display currency for the player, balances will be shown in this currency.
 * @param {*} currency
 * @param {*} playerSessionID
 * @returns
 */
export var setActiveCurrency = function setActiveCurrency(currency, playerSessionID) {
  return postProtected('/player/active-currency?currency=' + currency, playerSessionID);
};

/**
 * Set the expected annual activity for the player.
 * @param {number} expectedAnnualActivityFrom
 * @param {number} expectedAnnualActivityTo
 * @param {string} playerSessionID
 * @returns
 */
export var setExpectedAnnualActivity = function setExpectedAnnualActivity(playerSessionID, expectedAnnualActivityFrom, expectedAnnualActivityTo) {
  return postProtected('/kyc/source-of-wealth/v2', playerSessionID, {
    IncomeFromValue: expectedAnnualActivityFrom,
    IncomeToValue: expectedAnnualActivityTo,
    Type: 'ExpectedAnnualActivity'
  });
};

export default {
  signUp: signUp,
  signUpV2: signUpV2,
  signUpWithCode: signUpWithCode,
  signUpWithToken: signUpWithToken,
  update: update,
  userInfo: userInfo,
  changePassword: changePassword,
  signIn: signIn,
  signInV2: signInV2,
  signInWithCode: signInWithCode,
  createSSOToken: createSSOToken,
  signOut: signOut,
  updatePlayerTac: updatePlayerTac,
  requestPasswordReset: requestPasswordReset,
  resetPassword: resetPassword,
  activate: activate,
  requestActivationCode: requestActivationCode,
  getTags: getTags,
  addTag: addTag,
  removeTag: removeTag,
  getOptInOuts: getOptInOuts,
  optIn: optIn,
  optOut: optOut,
  optOutChoices: optOutChoices,
  bonusOptIn: bonusOptIn,
  tournamentOptIn: tournamentOptIn,
  changeUsername: changeUsername,
  changeNickname: changeNickname,
  paymentStats: paymentStats,
  lifetimeStats: lifetimeStats,
  getOptOutInsByToken: getOptOutInsByToken,
  validateCpr: validateCpr,
  setEmailPhone: setEmailPhone,
  setDetailsWithoutPassword: setDetailsWithoutPassword,
  verifyExistingSePlayer: verifyExistingSePlayer,
  verifyExistingSePlayerWithToken: verifyExistingSePlayerWithToken,
  validateUsername: validateUsername,
  validateEmail: validateEmail,
  validatePhone: validatePhone,
  validatePersonIdv2: validatePersonIdv2,
  validateSupportNumber: validateSupportNumber,
  getDisplayPages: getDisplayPages,
  getActiveCurrency: getActiveCurrency,
  setActiveCurrency: setActiveCurrency,
  setExpectedAnnualActivity: setExpectedAnnualActivity
};